// @flow
import WebComponent from 'fg-frontend/src/webComponents/WebComponent';

/**
 * Objednávka darčekového poukazu na detaile obchodu.
 *
 * Prepočítava súhrn priamo v prehliadači - držiteľ vidí, koľko zaplatí a čo mu zostane, ešte
 * než odošle formulár. Záväzne sa suma počíta až na serveri pri založení objednávky, toto je
 * len náhľad.
 */

const fragments = {
	DENOMINATION: '[data-fragment="GiftVoucherOrder.denomination"]',
	DENOMINATION_CHECKED: '[data-fragment="GiftVoucherOrder.denomination"]:checked',
	QUANTITY: '[data-fragment="GiftVoucherOrder.quantity"]',
	DECREASE: '[data-fragment="GiftVoucherOrder.decrease"]',
	INCREASE: '[data-fragment="GiftVoucherOrder.increase"]',
	ITEMS_LABEL: '[data-fragment="GiftVoucherOrder.itemsLabel"]',
	ITEMS_TOTAL: '[data-fragment="GiftVoucherOrder.itemsTotal"]',
	PAY_TOTAL: '[data-fragment="GiftVoucherOrder.payTotal"]',
	BALANCE_AFTER: '[data-fragment="GiftVoucherOrder.balanceAfter"]',
	BALANCE_AFTER_LABEL: '[data-fragment="GiftVoucherOrder.balanceAfterLabel"]',
	INSUFFICIENT: '[data-fragment="GiftVoucherOrder.insufficient"]',
	NOTICE_CARD_NOT_USABLE: '[data-fragment="GiftVoucherOrder.noticeCardNotUsable"]',
	NOTICE_TOP_UP: '[data-fragment="GiftVoucherOrder.noticeTopUp"]',
	NOTICE_NOT_PAIRED: '[data-fragment="GiftVoucherOrder.noticeNotPaired"]',
	NOTICE_NOT_ALLOWED: '[data-fragment="GiftVoucherOrder.noticeNotAllowed"]',
	NOTICE_LIMIT: '[data-fragment="GiftVoucherOrder.noticeLimit"]',
	SUBMIT: '.gift-voucher-order__submit',
};

const NEGATIVE_CLASS = 'gift-voucher-order__value--negative';
const INSUFFICIENT_CLASS = 'gift-voucher-order--insufficient';
const LOCALE = 'sk-SK';
const MIN_QUANTITY = 1;
// Polovica centu. Server počíta v BigDecimale, prehliadač v doubli - bez tejto rezervy by
// nominál, ktorý presne vyčerpá zostatok, mohol vyjsť o 10^-14 do mínusu, zamknúť tlačidlo
// a hlásiť "chýba 0,00 €".
const ROUNDING_TOLERANCE = 0.005;
// Stav doplatku z napárovanej platobnej karty (#434), ako ho vyhodnotil server.
const TOP_UP_AVAILABLE = 'AVAILABLE';
const TOP_UP_NOT_ALLOWED = 'NOT_ALLOWED';
const TOP_UP_NOT_PAIRED = 'NOT_PAIRED';

export default class GiftVoucherOrder extends WebComponent {
	didMount() {
		this.quantity = this.element.querySelector(fragments.QUANTITY);
		this.itemsLabel = this.element.querySelector(fragments.ITEMS_LABEL);
		this.itemsTotal = this.element.querySelector(fragments.ITEMS_TOTAL);
		this.payTotal = this.element.querySelector(fragments.PAY_TOTAL);
		this.balanceAfter = this.element.querySelector(fragments.BALANCE_AFTER);
		this.balanceAfterLabel = this.element.querySelector(fragments.BALANCE_AFTER_LABEL);
		this.insufficient = this.element.querySelector(fragments.INSUFFICIENT);
		this.submit = this.element.querySelector(fragments.SUBMIT);
		this.noticeCardNotUsable = this.element.querySelector(fragments.NOTICE_CARD_NOT_USABLE);
		this.noticeTopUp = this.element.querySelector(fragments.NOTICE_TOP_UP);
		this.noticeNotPaired = this.element.querySelector(fragments.NOTICE_NOT_PAIRED);
		this.noticeNotAllowed = this.element.querySelector(fragments.NOTICE_NOT_ALLOWED);
		this.noticeLimit = this.element.querySelector(fragments.NOTICE_LIMIT);

		this.brandName = this.element.dataset.brandName || '';
		this.balance = parseFloat(this.element.dataset.balance) || 0;
		// Vzor vety o kusoch príde zo šablóny, aby text zostal v properties a nie tu.
		this.itemsPattern = this.element.dataset.itemsLabel || '';
		// Posledný riadok súhrnu sa prepína medzi zostatkom po nákupe a chýbajúcou sumou,
		// oba texty preto chodia zo šablóny rovnako ako vzor vety o kusoch.
		this.balanceAfterText = this.element.dataset.balanceAfterLabel || '';
		this.missingText = this.element.dataset.missingLabel || '';
		// Doplatok z platobnej karty: napárovanie sa počas života stránky nemení, limit sa však
		// porovnáva s chýbajúcou sumou, ktorá sa mení s každou dlaždicou a počtom kusov.
		this.topUpState = this.element.dataset.topUpState || TOP_UP_NOT_PAIRED;
		// Expirovaná alebo zablokovaná karta platiť nemôže; nákup je zamknutý bez ohľadu na zostatok.
		this.cardUsable = this.element.dataset.cardUsable !== 'false';
		this.topUpLimit = parseFloat(this.element.dataset.topUpLimit) || 0;
		this.topUpPattern = this.element.dataset.topUpLabel || '';

		this.on('change', fragments.DENOMINATION, this.recalculate);
		this.on('input', fragments.QUANTITY, this.recalculate);
		this.on('click', fragments.DECREASE, () => this.step(-1));
		this.on('click', fragments.INCREASE, () => this.step(1));

		this.recalculate();
	}

	/** Zmení počet kusov o krok a prepočíta súhrn. Pod jeden kus sa ísť nedá. */
	step = (by) => {
		if (!this.quantity) {
			return;
		}
		this.quantity.value = String(Math.max(MIN_QUANTITY, this.readQuantity() + by));
		this.recalculate();
	};

	readQuantity = () => {
		const parsed = parseInt(this.quantity ? this.quantity.value : '', 10);
		return Number.isNaN(parsed) || parsed < MIN_QUANTITY ? MIN_QUANTITY : parsed;
	};

	readNominal = () => {
		const selected = this.element.querySelector(fragments.DENOMINATION_CHECKED);
		return selected ? parseFloat(selected.dataset.nominal) || 0 : 0;
	};

	recalculate = () => {
		const nominal = this.readNominal();
		const quantity = this.readQuantity();
		const total = nominal * quantity;
		const remaining = this.balance - total;

		if (this.itemsLabel) {
			this.itemsLabel.textContent = this.itemsPattern
				.replace('%quantity%', String(quantity))
				.replace('%brand%', this.brandName)
				.replace('%nominal%', this.formatNominal(nominal));
		}
		if (this.itemsTotal) {
			this.itemsTotal.textContent = this.formatAmount(total);
		}
		if (this.payTotal) {
			this.payTotal.textContent = this.formatAmount(total);
		}
		// Pri nedostatku sa podľa návrhu neukazuje záporný zostatok, ale chýbajúca suma -
		// to isté číslo dvakrát (raz so znamienkom, raz bez) by len mýlilo.
		const short = remaining < -ROUNDING_TOLERANCE;

		if (this.balanceAfter) {
			this.balanceAfter.textContent = this.formatAmount(short ? -remaining : remaining);
			this.balanceAfter.classList.toggle(NEGATIVE_CLASS, short);
		}
		if (this.balanceAfterLabel) {
			this.balanceAfterLabel.textContent = short ? this.missingText : this.balanceAfterText;
			this.balanceAfterLabel.classList.toggle(NEGATIVE_CLASS, short);
		}
		const missing = short ? -remaining : 0;
		const coveredByTopUp = short && this.topUpState === TOP_UP_AVAILABLE
			&& missing <= this.topUpLimit + ROUNDING_TOLERANCE;
		const locked = !this.cardUsable || (short && !coveredByTopUp);

		if (this.insufficient) {
			this.insufficient.hidden = this.cardUsable && !short;
		}
		this.updateNotices(missing, coveredByTopUp);
		// Zamknutie tlačidla je len rýchla spätná väzba; objednávku, ktorú nepokryje zostatok ani
		// doplatok z platobnej karty, odmieta server, takže vypnuté JS nákup neumožní.
		this.element.classList.toggle(INSUFFICIENT_CLASS, locked);
		// Modifikátor vyššie tlačidlo len prefarbí a zhasne mu kurzor. Samotné `disabled` je
		// tu preto, že tlačidlo zostáva `type="submit"` - bez neho ho ide zaostriť tabulátorom
		// a Enter v poli s počtom kusov formulár odošle, hoci vyzerá zamknuto.
		if (this.submit) {
			this.submit.disabled = locked;
		}
	};

	/** Z podôb žltého rámčeka nechá viditeľnú práve tú, ktorá platí. */
	updateNotices = (missing, coveredByTopUp) => {
		if (this.noticeCardNotUsable) {
			this.noticeCardNotUsable.hidden = this.cardUsable;
		}
		if (!this.cardUsable) {
			// Hláška o nepoužiteľnej karte prebije všetky ostatné - nedostatok ani doplatok tu
			// nič neriešia.
			[this.noticeTopUp, this.noticeNotPaired, this.noticeNotAllowed, this.noticeLimit]
				.filter(Boolean)
				.forEach((notice) => {
					notice.hidden = true;
				});
			return;
		}
		if (this.noticeTopUp) {
			this.noticeTopUp.hidden = !coveredByTopUp;
			if (coveredByTopUp) {
				this.noticeTopUp.textContent = this.topUpPattern.replace('%missing%', this.formatAmount(missing));
			}
		}
		if (this.noticeNotPaired) {
			this.noticeNotPaired.hidden = this.topUpState !== TOP_UP_NOT_PAIRED;
		}
		if (this.noticeNotAllowed) {
			this.noticeNotAllowed.hidden = this.topUpState !== TOP_UP_NOT_ALLOWED;
		}
		if (this.noticeLimit) {
			this.noticeLimit.hidden = this.topUpState !== TOP_UP_AVAILABLE || coveredByTopUp;
		}
	};

	/** Nominál bez zbytočných desatinných miest, tak ako na dlaždiciach: 20 €, nie 20,00 €. */
	formatNominal = (value) => value.toLocaleString(LOCALE, {maximumFractionDigits: 2});

	formatAmount = (value) => `${value.toLocaleString(LOCALE, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})} €`;
}



// WEBPACK FOOTER //
// ./config/module-publish/resources/js/components/GiftVoucherOrder.js